import type { INewsMarker, INewsSentiment } from "~/feature/chart/types";

export interface INewsDot {
  key: string;
  leftPercent: number;
  markers: INewsMarker[];
  sentiment: INewsSentiment | null;
}

interface ITimeAxis {
  startsAt: number;
  endsAt: number;
}

interface IPlacedMarker {
  leftPercent: number;
  marker: INewsMarker;
}

const publishedTime = (marker: INewsMarker) => Date.parse(marker.publishedAt);

const getTimeAxis = (timeline: number[]): ITimeAxis | null => {
  const startsAt = timeline.at(0);
  const endsAt = timeline.at(-1);

  if (startsAt === undefined || endsAt === undefined || endsAt <= startsAt) {
    return null;
  }

  return { startsAt, endsAt };
};

const isOnAxis = (marker: INewsMarker, { startsAt, endsAt }: ITimeAxis) =>
  publishedTime(marker) >= startsAt && publishedTime(marker) <= endsAt;

const getLeftPercent = (marker: INewsMarker, { startsAt, endsAt }: ITimeAxis) =>
  ((publishedTime(marker) - startsAt) / (endsAt - startsAt)) * 100;

const oneMarkerPerArticle = (news: INewsMarker[]) => {
  const byArticleId = new Map<string, INewsMarker>();
  const sortedByMarket = [...news].sort((a, b) =>
    a.marketId.localeCompare(b.marketId)
  );

  for (const marker of sortedByMarket) {
    if (!byArticleId.has(marker.id)) {
      byArticleId.set(marker.id, marker);
    }
  }

  return [...byArticleId.values()];
};

const getGroupSentiment = (markers: INewsMarker[]): INewsSentiment | null => {
  const [first, ...rest] = markers;
  const sentiment = first.sentiment ?? null;

  return rest.every((marker) => (marker.sentiment ?? null) === sentiment)
    ? sentiment
    : null;
};

const toDot = (group: IPlacedMarker[]): INewsDot => {
  const first = group[0];
  const last = group[group.length - 1];
  const markers = group.map(({ marker }) => marker).reverse();

  return {
    key: first.marker.id,
    leftPercent: (first.leftPercent + last.leftPercent) / 2,
    markers,
    sentiment: getGroupSentiment(markers),
  };
};

const groupByGap = (leftToRight: IPlacedMarker[], minGapPercent: number) => {
  const groups: IPlacedMarker[][] = [];

  for (const placed of leftToRight) {
    const group = groups.at(-1);
    const anchor = group?.at(0);

    if (
      group &&
      anchor &&
      placed.leftPercent - anchor.leftPercent <= minGapPercent
    ) {
      group.push(placed);
    } else {
      groups.push([placed]);
    }
  }

  return groups;
};

export const buildNewsDots = (
  news: INewsMarker[],
  timeline: number[],
  minGapPercent: number
): INewsDot[] => {
  const axis = getTimeAxis(timeline);

  if (!axis) {
    return [];
  }

  const onAxis = news.filter((marker) => isOnAxis(marker, axis));
  const leftToRight = oneMarkerPerArticle(onAxis)
    .map((marker) => ({ leftPercent: getLeftPercent(marker, axis), marker }))
    .sort(
      (a, b) =>
        a.leftPercent - b.leftPercent || a.marker.id.localeCompare(b.marker.id)
    );

  return groupByGap(leftToRight, minGapPercent).map(toDot);
};
